import React from "react";
import {useAuth0} from "@auth0/auth0-react";
import { isStaffEmail } from "@utils/user_utils";

/**
 * Error boundary that catches render-time errors in child components.
 * Use this to wrap individual charts or chart sections so that a single
 * chart failure doesn't crash the entire page.
 *
 * Props:
 *  - fallback       (ReactNode)   Optional custom fallback UI.
 *  - onError        (Function)    Optional callback: (error, errorInfo) => void
 *  - componentLabel (string)      A human-readable label used in the default
 *                                 fallback message (e.g. "GPS Section" or a metric name).
 */
class ChartErrorBoundaryInner extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log to console so it's visible during development
        console.error(
            `[ChartErrorBoundary${this.props.componentLabel ? ` – ${this.props.componentLabel}` : ""}] Caught error:`,
            error,
            errorInfo
        );

        // Forward to an optional external handler (e.g. Sentry, Datadog, etc.)
        if (typeof this.props.onError === "function") {
            this.props.onError(error, errorInfo);
        }
    }

    render() {
        if (this.state.hasError) {
            // If a custom fallback was provided, render that instead
            if (this.props.fallback) {
                return this.props.fallback;
            }

            if (!this.props.isStaff) {
                return <></>
            }

            console.log("Merged Options: ", this.props?.mergedOptions);
            const label = this.props.componentLabel
                ? `"${this.props.componentLabel}"`
                : "This chart";

            return (
                <div style={styles.container}>
                    <div style={styles.inner}>
                        <span style={styles.icon}>⚠️</span>
                        <p style={styles.heading}>
                            {label} failed to render
                        </p>

                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const ChartErrorBoundary = ({ children, ...props }) => {
    const { user } = useAuth0();

    const isStaff = isStaffEmail(user?.email);

    return (
        <ChartErrorBoundaryInner {...props} isStaff={isStaff}>
            {children}
        </ChartErrorBoundaryInner>
    );
};


const styles = {
    container: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        minHeight: "200px",
        padding: "24px",
        boxSizing: "border-box",
    },
    inner: {
        textAlign: "center",
        maxWidth: "360px",
    },
    icon: {
        fontSize: "28px",
        display: "block",
        marginBottom: "8px",
    },
    heading: {
        margin: "0 0 4px",
        fontSize: "14px",
        fontWeight: 600,
        color: "#333",
    },
    detail: {
        margin: "0 0 12px",
        fontSize: "12px",
        color: "#888",
        wordBreak: "break-word",
    },
    retryButton: {
        padding: "6px 16px",
        fontSize: "12px",
        fontWeight: 500,
        border: "1px solid #ccc",
        borderRadius: "4px",
        background: "#f0f0f0",
        cursor: "pointer",
        color: "#333",
        transition: "background 0.15s ease",
    },
};

export default ChartErrorBoundary;